import {useMemo, useState} from 'react'
import {Controller, useController, useFormContext, useWatch} from 'react-hook-form'
import {useTheme} from 'styled-components'
import dayjs from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {InfoCircleIcon} from '@/components/ui/icon/Icon'
import {Switch} from '@/components/commons/switch/Switch'
import {InputTime, DEFAULT_AVAILABILITY_OPTIONS} from '@/components/commons/input-time/InputTime'
import {ScheduledConsultantEventCount} from '../scheduled-consultant-event-count/ScheduledConsultantEventCount'
import {AddConsultantAbsenceFormValidationSchema} from '../../formSchemas'
import {DEFAULT_END_TIME, DEFAULT_TIME_INTERVAL, DEFAULT_START_TIME} from '@/features/users-availability/utils'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {useGetEvents} from '../../queries/useGetEvents'

export const AddConsultantAbsenceDateTimeFields = () => {
    const {t} = useTranslation()
    const {control, resetField} = useFormContext<AddConsultantAbsenceFormValidationSchema>()
    const {palette} = useTheme()

    const consultant = useWatch({control, name: 'consultant'})
    const absenceTime = useWatch({control, name: 'absenceTime'})

    const startDateController = useController({control, name: 'startDate'})
    const endDateController = useController({control, name: 'endDate'})
    const startTimeController = useController({control, name: 'startTime'})
    const endTimeController = useController({control, name: 'endTime'})

    const isSameDay = useMemo(() => {
        return dayjs(startDateController.field.value).isSame(dayjs(endDateController.field.value), 'day')
    }, [startDateController.field.value, endDateController.field.value])

    const showAbsenceTimeWarning = useMemo(() => {
        return !isSameDay && absenceTime && startDateController.field.value && endDateController.field.value
    }, [isSameDay, absenceTime, startDateController.field.value, endDateController.field.value])

    const [month, setMonth] = useState<Date>(startDateController.field.value ?? new Date())

    const {data: patientsEvents} = useGetEvents({
        params: {
            startDate: dayjs(month).startOf('month').format('YYYY-MM-DD'),
            endDate: dayjs(month).endOf('month').format('YYYY-MM-DD'),
            therapists: consultant ? [consultant.id] : [],
            patients: []
        },
        options: {
            enabled: !!consultant
        }
    })

    const consultantAbsences = useMemo(() => {
        return (
            patientsEvents
                ?.filter(patientEvent => patientEvent.type == 'consultant_absence')
                .map(patientEvent => dayjs(patientEvent.date).toDate()) || []
        )
    }, [patientsEvents])

    return (
        <>
            <GridItem spanColumn={2}>
                <Grid columns={2} gap={4} fullWidth>
                    <InputDate
                        value={startDateController.field.value}
                        onChange={startDateController.field.onChange}
                        fullWidth
                        name="startDate"
                        label={t('agenda:add_absence_start_date')}
                        disableAfterDate={endDateController.field.value}
                        errorMessage={startDateController.fieldState.error?.message}
                        placeholder={'GG/MM/AAAA'}
                        disabled={!consultant}
                        onMonthChange={setMonth}
                        disabledDates={consultantAbsences}
                    />

                    <InputDate
                        value={endDateController.field.value}
                        onChange={endDateController.field.onChange}
                        fullWidth
                        name="endDate"
                        label={t('agenda:add_absence_end_date')}
                        disableBeforeDate={startDateController.field.value}
                        errorMessage={endDateController.fieldState.error?.message}
                        placeholder={'GG/MM/AAAA'}
                        disabled={!consultant}
                        onMonthChange={setMonth}
                        disabledDates={consultantAbsences}
                    />
                </Grid>
            </GridItem>

            <GridItem spanColumn={1}>
                <Flexbox gap={2} direction="column">
                    <Controller
                        control={control}
                        name="absenceTime"
                        render={({field}) => (
                            <Switch
                                size="sm"
                                label={t('agenda:absence_time')}
                                checked={field.value}
                                onChange={checked => {
                                    resetField('startTime')
                                    resetField('endTime')
                                    field.onChange(checked)
                                }}
                            />
                        )}
                    />
                    {showAbsenceTimeWarning && (
                        <Flexbox align="center" gap={1}>
                            <InfoCircleIcon size={16} color={palette.warning[500]} />
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:absence_time_warning')}
                            </Text>
                        </Flexbox>
                    )}
                </Flexbox>
            </GridItem>

            <ScheduledConsultantEventCount />

            {absenceTime && (
                <GridItem spanColumn={2}>
                    <Grid columns={2} gap={4} fullWidth>
                        <InputTime
                            value={startTimeController.field.value}
                            onChange={value => {
                                startTimeController.field.onChange(value)
                                resetField('endTime')
                            }}
                            fullWidth
                            name="startTime"
                            label={t('agenda:absence_start_time')}
                            placeholder={t('agenda:select_time')}
                            errorMessage={startTimeController.fieldState.error?.message}
                            startTime={DEFAULT_START_TIME}
                            endTime={DEFAULT_END_TIME}
                            interval={DEFAULT_TIME_INTERVAL}
                            availableSlots={DEFAULT_AVAILABILITY_OPTIONS}
                            disabled={!consultant}
                        />

                        <InputTime
                            value={endTimeController.field.value}
                            onChange={endTimeController.field.onChange}
                            fullWidth
                            name="endTime"
                            label={t('agenda:absence_end_time')}
                            placeholder={t('agenda:select_time')}
                            errorMessage={endTimeController.fieldState.error?.message}
                            startTime={
                                DEFAULT_AVAILABILITY_OPTIONS[
                                    DEFAULT_AVAILABILITY_OPTIONS.findIndex(
                                        slot => slot === startTimeController.field.value
                                    ) + 1
                                ]
                            }
                            endTime={DEFAULT_END_TIME}
                            interval={DEFAULT_TIME_INTERVAL}
                            availableSlots={DEFAULT_AVAILABILITY_OPTIONS}
                            disabled={!consultant}
                        />
                    </Grid>
                </GridItem>
            )}
        </>
    )
}
